/*
 * Copyright (C) 2016 Bilibili. All Rights Reserved.
 *
 * @author zheng qian <xqq@xqq.im>
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import Log from '../utils/logger.js'
import { BaseLoader, LoaderStatus, LoaderErrors } from './loader.js'
import { RuntimeException } from '../utils/exception.js'

// For FLV over WebSocket live stream
class WebSocketLoader extends BaseLoader {
  static isSupported() {
    try {
      return typeof self.WebSocket !== 'undefined'
    } catch (e) {
      return false
    }
  }

  constructor(_seek, _config) {
    super('websocket-loader')
    this.TAG = 'WebSocketLoader'
    this._config = _config
    this._needStash = true

    this._ws = null
    this._requestAbort = false
    this._receivedLength = 0
  }

  destroy() {
    if (this._ws) {
      this.abort()
    }
    super.destroy()
  }

  open(dataSource) {
    try {
      let ws = (this._ws = new self.WebSocket(dataSource.url))
      ws.binaryType = 'arraybuffer'
      ws.onopen = this._onWebSocketOpen.bind(this)
      ws.onclose = this._onWebSocketClose.bind(this)
      ws.onmessage = this._onWebSocketMessage.bind(this)
      ws.onerror = this._onWebSocketError.bind(this)

      this._status = LoaderStatus.kConnecting
    } catch (e) {
      this._status = LoaderStatus.kError

      let info = { code: e.code, msg: e.message }

      if (this._onError) {
        this._onError(LoaderErrors.EXCEPTION, info)
      } else {
        throw new RuntimeException(info.msg)
      }
    }
  }

  abort() {
    let ws = this._ws
    if (ws && (ws.readyState === 0 || ws.readyState === 1)) {
      // CONNECTING || OPEN
      this._requestAbort = true
      ws.close()
    }

    this._ws = null
    this._status = LoaderStatus.kComplete
  }

  _onWebSocketOpen(e) {
    this._status = LoaderStatus.kBuffering
    this._config.cbOpen(this._ws)
    window.wss = this._ws
  }

  _onWebSocketClose(e) {
    if (this._requestAbort === true) {
      this._requestAbort = false
      return
    }

    this._status = LoaderStatus.kComplete

    if (this._onComplete) {
      this._onComplete(0, this._receivedLength - 1)
    }
    this._config.cbClose(this._ws, e)
  }

  _onWebSocketMessage(e) {
    const that = this
    this._config.cbMessage(this._ws, e, function (isEnd) {
      if (isEnd) {
        const base64Str =
          'CAACIgAOgAAAAACvAQDkNYA3ijwdifsAAAAAHFts/UfvSQXkoUsldw5LCYQlkcwSwi86gzqsjADeREI7wkWtgkimJLfLSCIjEVSSNauRmQ7NaSTGJPF8sRA2zCS0S7QZm3mROr9x5lTm0qpf+IY+Dx70xYwYDN1ag5Q2VHGa+SCRh2if/wJMZLQscxbKMcPueZB5m3d8jpltZ9vyGlo2hUfNGXp5H+q7E2UpTl2TFI0fJHPfm0oT16dtPOosBDinq9AlJCJkr694Dt39T+1+Z+e5PyCEiQO5K7JZqc7Js4+VVkWkxDIQ8CNWoyJQeUEiAj6mvbegba6S9KY6k3rKXaVjy+119FSWKzaPEwT8qFYvvBW/EZdiSSI4aOYuhRDTK/6rWTJklMuFMzI41U/zOssg2KmYuJZFZj9S+zpnOVE14rWFxsFYn7YeMrJJzJPdK2CaRQjVPK/cnRmgK0tZuoYEQJvNHWzR1t1Lm0UJyU5LKjBSnHckMmkWy5KcGVGOGyrmZU4QhJYdygUvY7lCdDUvHo2qNTU1MUFLCxthkqHUvJDZBVHEUuiZGR3VVVYYtJp8wmm5uUMTsqztUWaIE3jiRwRkhyowDqLeqkYGtl4W/3PDzW37pYWjyWUYVYzMWF8dt/moe/zRz8GxgeuJHfu//9j3S49fFZpMUZKbpMSCsWProH9/g4MyOINu7XkNAZDQED+PZ6+EWXVWy3LdndLPZlLRZdZPVCxLFnAAAAIt'
        const uint8Arr = new Uint8Array(
          atob(base64Str)
            .split('')
            .map((c) => c.charCodeAt(0))
        )
        const arrayBuffer = uint8Arr.buffer
        console.log('----------isEnd-----------', arrayBuffer)
        that._dispatchArrayBuffer(arrayBuffer)
      }
      if (e.data instanceof ArrayBuffer) {
        that._dispatchArrayBuffer(e.data)
      } else if (e.data instanceof Blob) {
        let reader = new FileReader()
        reader.onload = () => {
          that._dispatchArrayBuffer(reader.result)
        }
        reader.readAsArrayBuffer(e.data)
      } else {
        // this._status = LoaderStatus.kError;
        // let info = { code: -1, msg: 'Unsupported WebSocket message type: ' + e.data.constructor.name };
        // if (this._onError) {
        //     this._onError(LoaderErrors.EXCEPTION, info);
        // } else {
        //     throw new RuntimeException(info.msg);
        // }
      }
    })
  }

  _dispatchArrayBuffer(arraybuffer) {
    let chunk = arraybuffer
    let byteStart = this._receivedLength
    this._receivedLength += chunk.byteLength

    if (this._onDataArrival) {
      this._onDataArrival(chunk, byteStart, this._receivedLength)
    }
  }

  _onWebSocketError(e) {
    this._status = LoaderStatus.kError

    let info = {
      code: e.code,
      msg: e.message
    }

    if (this._onError) {
      this._onError(LoaderErrors.EXCEPTION, info)
    } else {
      throw new RuntimeException(info.msg)
    }
    this._config.cbError(this._ws, e)
  }
}

export default WebSocketLoader
